import Navigation from '../components/Navigation.jsx'
import Footer from '../components/Footer.jsx'

export default function BotInfoPage() {
    return (
        <div className="min-h-screen flex flex-col">
            <Navigation />

            <main className="flex-1 bg-gray-50 py-8">
                <div className="max-w-4xl mx-auto px-4">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900">MakeMerry Web Crawler</h1>
                        <p className="text-gray-600 mt-1">Information about our data collection practices</p>
                    </div>

                    {/* Main Content */}
                    <div className="bg-white rounded-lg shadow-sm p-8">
                        <div className="prose max-w-none">
                            {/* Overview Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    MakeMerry operates a web crawler (bot) that automatically discovers and collects
                                    information about upcoming events and fun activities from publicly available sources
                                    across the internet.
                                </p>
                            </section>

                            {/* Purpose Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Purpose</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Our crawler's primary purpose is to connect potential customers with businesses and
                                    service providers by aggregating and presenting information about local events,
                                    activities, and venues in one convenient location.
                                </p>
                                <p className="text-gray-700 leading-relaxed">
                                    This helps users discover exciting opportunities in their area while helping businesses
                                    reach interested customers.
                                </p>
                            </section>

                            {/* What We Collect Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">What We Collect</h2>
                                <p className="text-gray-700 leading-relaxed mb-3">
                                    Our crawler collects publicly available information including:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                    <li>Event names, dates, times, and locations</li>
                                    <li>Venue information and contact details</li>
                                    <li>Event descriptions and categories</li>
                                    <li>Publicly posted images and promotional materials</li>
                                    <li>Pricing and ticketing information</li>
                                </ul>
                            </section>

                            {/* AI Training Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">AI and Machine Learning</h2>
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                                    <p className="text-gray-900 font-semibold mb-2">Important Notice:</p>
                                    <p className="text-gray-700">
                                        We do <strong>not</strong> use collected data to train large language models (LLMs).
                                        All data we collect is used exclusively
                                        for the purpose of connecting users with events and businesses through our platform.
                                    </p>
                                </div>
                            </section>

                            {/* Respecting Robots.txt Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Respecting Website Owners</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    MakeMerry's crawler respects the <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">robots.txt</code> standard
                                    and follows all directives specified by website owners. If you do not want our crawler
                                    to access your website, you can block it using your robots.txt file.
                                </p>
                                <p className="text-gray-700 leading-relaxed mb-3">
                                    In addition to robots.txt, our crawler also respects the following META tags:
                                </p>
                                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                                    <li>
                                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">noindex</code> -
                                        Prevents the page from being indexed and stored in our database
                                    </li>
                                    <li>
                                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">nofollow</code> -
                                        Prevents our crawler from following links on the page
                                    </li>
                                </ul>
                                <p className="text-gray-700 leading-relaxed">
                                    You can add these tags to your HTML pages to control how our crawler interacts with your content:
                                </p>
                                <div className="bg-gray-100 rounded-lg p-4 mt-3">
                                    <code className="text-sm font-mono text-gray-800 block">&lt;meta name="robots" content="noindex, nofollow"&gt;</code>
                                </div>
                            </section>

                            {/* Bot Identification Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Bot Identification</h2>
                                <p className="text-gray-700 leading-relaxed mb-3">
                                    Our crawler identifies itself with the following user agent:
                                </p>
                                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                                    <code className="text-sm font-mono text-gray-800">Mozilla/5.0 (compatible; MakeMerryBot/1.0; +https://makemerry.app/bots)</code>
                                </div>
                            </section>

                            {/* Contact Section */}
                            <section className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions or Concerns?</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    If you have questions about our crawler, would like to report an issue, or need to
                                    request removal of specific content, please contact us:
                                </p>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-gray-700">
                                        <strong>Email:</strong>{' '}
                                        <a href="mailto:support@makemerry.app" className="text-primary hover:underline">
                                            support@makemerry.app
                                        </a>
                                    </p>
                                </div>
                            </section>

                            {/* Last Updated */}
                            <section className="pt-6 border-t border-gray-200">
                                <p className="text-sm text-gray-500">
                                    Last updated: {new Date().toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            </section>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    )
}
